import type { DefLookup } from "./catalog";
import type { SimulateContext } from "./cable-current";
import { buildGraph, computeActiveSourceCircuits, reachable, v, type Graph, type Vertex } from "./diagnostics/graph";
import { dcPortTerminalGroups } from "./component-ports";
import { getEffectiveStateOption } from "./simulate/states";
import type { System } from "./types";

export type TerminalPotentials = {
  /** Signed potential difference. Undefined for unrelated or inconsistent sources. */
  voltageBetween(positive: Vertex, negative: Vertex): number | undefined;
};

/** Resolve source-established potential differences, not a bank's maximum
 * voltage. Copper joins equipotential regions; a battery adds a voltage
 * constraint between two different regions. Keeping those operations separate
 * preserves midpoint taps, shared grounds and series/parallel combinations.
 * A declared converter output establishes a batteryless output rail. Battery
 * constraints take precedence over converter nameplates on battery-backed rails.
 * This is nominal voltage only: it does not solve impedance or charging taper.
 * Pass the live conductor graph to respect open switches during simulation. */
export function resolveTerminalPotentials(
  system: System,
  lookup: DefLookup,
  graph: Graph = buildGraph(system, lookup, { excludeSense: true }),
  simulate?: SimulateContext,
): TerminalPotentials {
  const regions = new Map<Vertex, number>();
  let nextRegion = 0;
  const region = (vertex: Vertex): number => {
    const known = regions.get(vertex);
    if (known != null) return known;
    const id = nextRegion++;
    for (const member of reachable(graph, vertex)) regions.set(member, id);
    return id;
  };
  const constraints = new Map<number, Array<{ to: number; rise: number }>>();
  const add = (from: number, to: number, rise: number) => {
    const edges = constraints.get(from) ?? [];
    edges.push({ to, rise });
    constraints.set(from, edges);
  };
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (def?.category !== "battery" || def.voltage == null || !Number.isFinite(def.voltage) || def.voltage <= 0) continue;
    const state = getEffectiveStateOption(def, node.id, simulate);
    if (state?.ampOverride === 0 || state?.ampMultiplier === 0) continue;
    const positives = def.terminals.filter(t => t.polarity === "positive");
    const negatives = def.terminals.filter(t => t.polarity === "negative" || t.polarity === "ground");
    for (const p of positives) for (const n of negatives) {
      const pos = region(v(node.id, p.id)), neg = region(v(node.id, n.id));
      add(neg, pos, def.voltage);
      add(pos, neg, -def.voltage);
    }
  }
  const solve = () => {
    const potential = new Map<number, { network: number; volts: number }>();
    const inconsistent = new Set<number>();
    let network = 0;
    for (const seed of constraints.keys()) {
      if (potential.has(seed)) continue;
      const id = network++;
      potential.set(seed, { network: id, volts: 0 });
      const queue = [seed];
      for (let i = 0; i < queue.length; i++) {
        const from = queue[i], current = potential.get(from)!;
        for (const edge of constraints.get(from) ?? []) {
          const volts = current.volts + edge.rise;
          const known = potential.get(edge.to);
          if (known) {
            if (Math.abs(known.volts - volts) > 1e-6) inconsistent.add(id);
          } else {
            potential.set(edge.to, { network: id, volts });
            queue.push(edge.to);
          }
        }
      }
    }
    return { potential, inconsistent };
  };
  // Resolve batteries first so a nominal converter setting cannot overwrite
  // an actual bank voltage (for example a 12.8 V battery on a 12 V charger).
  const battery = solve();
  const activeConverters = simulate && new Set(computeActiveSourceCircuits(system, lookup, simulate)
    .flatMap(source => source.converterNodeId ? [source.converterNodeId] : []));
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (def?.category !== "charger" || def.outputVoltage == null || !Number.isFinite(def.outputVoltage) || def.outputVoltage <= 0) continue;
    if (activeConverters && !activeConverters.has(node.id)) continue;
    for (const group of dcPortTerminalGroups(def, "output")) {
      for (const p of group.filter(t => t.polarity === "positive")) {
        for (const n of group.filter(t => t.polarity === "negative" || t.polarity === "ground")) {
          const pos = region(v(node.id, p.id)), neg = region(v(node.id, n.id));
          const existingPos = battery.potential.get(pos), existingNeg = battery.potential.get(neg);
          if (pos === neg || (existingPos && existingNeg && existingPos.network === existingNeg.network)) continue;
          add(neg, pos, def.outputVoltage);
          add(pos, neg, -def.outputVoltage);
        }
      }
    }
  }
  const { potential, inconsistent } = solve();
  return {
    voltageBetween(positive, negative) {
      const pos = potential.get(region(positive)), neg = potential.get(region(negative));
      if (!pos || !neg || pos.network !== neg.network || inconsistent.has(pos.network)) return undefined;
      return pos.volts - neg.volts;
    },
  };
}
